/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const lastUpdatedDate = new Date(1707264000000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: null,
        description: null,
        lastUpdatedDate: "Wed Feb 07 2024 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: "sap-hana",
        type: "destination",
        icon: null,
        toc: [{
          "value": "Supported syncing",
          "depth": 0,
          "id": "supported-syncing"
        }, {
          "value": "Connect to SAP HANA",
          "depth": 0,
          "id": "connect-to-sap-hana"
        }, {
          "value": "Record matching",
          "depth": 0,
          "id": "record-matching"
        }, {
          "value": "Column types",
          "depth": 0,
          "id": "column-types"
        }, {
          "value": "Required permissions",
          "depth": 0,
          "id": "required-permissions"
        }, {
          "value": "Tips and troubleshooting",
          "depth": 0,
          "id": "tips-and-troubleshooting"
        }, {
          "value": "Common errors",
          "depth": 1,
          "id": "common-errors"
        }, {
          "value": "Live debugger",
          "depth": 1,
          "id": "live-debugger"
        }, {
          "value": "Sync alerts",
          "depth": 1,
          "id": "sync-alerts"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      h2: "h2",
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      p: "p",
      a: "a",
      strong: "strong",
      ul: "ul",
      li: "li",
      code: "code",
      h3: "h3"
    }, _provideComponents(), props.components), {CommonErrorsPreface, LiveDebugger, SyncAlerts} = _components;
    if (!CommonErrorsPreface) _missingMdxReference("CommonErrorsPreface", true);
    if (!LiveDebugger) _missingMdxReference("LiveDebugger", true);
    if (!SyncAlerts) _missingMdxReference("SyncAlerts", true);
    return _jsxs(_Fragment, {
      children: [_jsx(_components.h2, {
        id: "supported-syncing",
        children: "Supported syncing"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync Type"
            }), _jsx(_components.th, {
              children: "Description"
            }), _jsx(_components.th, {
              children: "Supported Sync Modes"
            })]
          })
        }), _jsx(_components.tbody, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Any data set"
            }), _jsx(_components.td, {
              children: "Sync data from any source to a HANA table"
            }), _jsx(_components.td, {
              children: "Upsert"
            })]
          })
        })]
      }), "\n", _jsx(_components.h2, {
        id: "connect-to-sap-hana",
        children: "Connect to SAP HANA"
      }), "\n", _jsxs(_components.p, {
        children: ["Go to the ", _jsxs(_components.a, {
          href: "https://app.hightouch.com/destinations",
          children: [_jsx(_components.strong, {
            children: "Destinations"
          }), " overview page"]
        }), " and click the ", _jsx(_components.strong, {
          children: "Add destination"
        }), " button. Select ", _jsx(_components.strong, {
          children: "SAP HANA"
        }), " and click ", _jsx(_components.strong, {
          children: "Continue"
        }), ". If you're not using a ", _jsx(_components.a, {
          href: "#tunneling",
          children: "tunnel"
        }), ", you can then authenticate Hightouch to SAP HANA by entering the following fields:"]
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Host"
          }), ": The hostname or IP address of your SAP HANA server. You don't need to include the ", _jsx(_components.code, {
            children: "https://"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Port"
          }), ": The port number of your SAP HANA server."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "User"
          }), ": The user that has access through Hightouch to the database and tables you want to sync to."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Password"
          }), ": The password for the user specified above."]
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: ["To ensure your ", _jsx(_components.a, {
          href: "#required-permissions",
          children: "credentials"
        }), " are correct, click ", _jsx(_components.strong, {
          children: "Test connection"
        }), ". This confirms if Hightouch can connect to your database by running a basic ", _jsx(_components.code, {
          children: "SELECT"
        }), " query."]
      }), "\n", _jsx(_components.h2, {
        id: "record-matching",
        children: "Record matching"
      }), "\n", _jsxs(_components.p, {
        children: ["Hightouch requires a unique identifier in the destination HANA table to add, remove, and update rows. The column must be either a ", _jsx(_components.code, {
          children: "UNIQUE"
        }), " or ", _jsx(_components.code, {
          children: "PRIMARY KEY"
        }), " column. At least one Hightouch field must map to a unique column."]
      }), "\n", _jsx(_components.p, {
        children: "If you map to an incompatible column, Hightouch will fail the sync with an error message."
      }), "\n", _jsx(_components.h2, {
        id: "column-types",
        children: "Column types"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch works out of the box with all standard column types."
      }), "\n", _jsx(_components.p, {
        children: "If you see type errors, it may be because your SQL query is producing the wrong format. Message us on Intercom if you need help."
      }), "\n", _jsx(_components.h2, {
        id: "required-permissions",
        children: "Required permissions"
      }), "\n", _jsx(_components.p, {
        children: "Your HANA credentials must be able to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.code, {
            children: "MERGE INTO"
          }), ", ", _jsx(_components.code, {
            children: "UPDATE"
          }), ", ", _jsx(_components.code, {
            children: "INSERT"
          }), " and ", _jsx(_components.code, {
            children: "DELETE"
          }), " rows from the destination table."]
        }), "\n", _jsx(_components.li, {
          children: "Create a local temporary table"
        }), "\n", _jsxs(_components.li, {
          children: ["Perform select operations on system tables:", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "SCHEMAS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "TABLES"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "TABLE_COLUMNS"
              })
            }), "\n", _jsx(_components.li, {
              children: _jsx(_components.code, {
                children: "CONSTRAINTS"
              })
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "tips-and-troubleshooting",
        children: "Tips and troubleshooting"
      }), "\n", _jsx(_components.h3, {
        id: "common-errors",
        children: "Common errors"
      }), "\n", _jsx(CommonErrorsPreface, {}), "\n", _jsx(_components.h3, {
        id: "live-debugger",
        children: "Live debugger"
      }), "\n", _jsx(LiveDebugger, {}), "\n", _jsx(_components.h3, {
        id: "sync-alerts",
        children: "Sync alerts"
      }), "\n", _jsx(SyncAlerts, {})]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
